/* ─────────────────────────────────────────────────────────────
   WORKS — client project roster + the four practice categories.
   Client engagements only. Our own products (SmartPost AI,
   Talent Cloud, Company GPT …) belong to the products sections
   and their /products/* pages, never to the works wall.

   Every row carries three explicit publish flags. There is no
   implicit default: a new row with no flags is invisible on both
   surfaces until someone sets them.

     founderApproved — the founder signed off on showing it publicly
     public          — the client permits being named
     showOnHome      — editorial pick for the home preview

   `featured` picks which project fronts its practice on the home
   preview. `representative` marks a capability-level entry rather
   than one named engagement — those trigger the disclosure line,
   the same pattern web-sys-selected-work.jsx already uses.

   Kept in sync with works-preview.jsx on the home page: same ids,
   labels, and hues so the home summary and the full wall agree.
   `mockups` ({ desktop, tablet, mobile } → /public paths) renders a
   photo plate on the home preview; rows without it keep the flat
   tinted panel.
   ───────────────────────────────────────────────────────────── */
const CATEGORIES = [
  { id: "ai",   label: "AI Solutions & Automation",  short: "AI",    hue: 270, accent: "var(--indigo)",       soft: "var(--indigo-soft)",  deep: "var(--indigo-deep)" },
  { id: "mvp",  label: "Product & MVP Development",   short: "MVP",   hue: 30,  accent: "oklch(62% 0.14 30)",  soft: "oklch(94% 0.04 30)",  deep: "oklch(38% 0.12 30)" },
  { id: "web",  label: "Web Systems & Applications",  short: "Web",   hue: 158, accent: "var(--moss)",         soft: "oklch(94% 0.04 158)", deep: "oklch(38% 0.10 158)" },
  { id: "uiux", label: "AI-Ready Websites & UI/UX",   short: "UI/UX", hue: 320, accent: "oklch(58% 0.14 320)", soft: "oklch(94% 0.04 320)", deep: "oklch(38% 0.14 320)" },
];

const CAT = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

/* Published everywhere. Spread onto each row rather than defaulted in
   code, so an unreviewed row fails closed instead of leaking. */
const PUBLISHED = { founderApproved: true, public: true, showOnHome: true };

/* Parked (2026-08-27, Release Clinic re-parked 2026-09-01): DXR,
   Release Diet, Release Clinic, Release Food —
   removed from the wall until their mockups and KB profiles land.
   More projects are coming; re-add rows here as they are ready. */

const PROJECTS = [
  /* AI Solutions & Automation */
  /* Next AI is our own assistant, not a client engagement — listed here
     deliberately as the flagship demo of the AI practice. */
  { ...PUBLISHED, cat: "ai", name: "Next AI", sector: "AI assistant",
    blurb: "AI-agent-backed public-facing assistant for Next Innovations customers — consultation, LINE AI setup, bookings, and quick actions in one conversational surface.",
    metric: "Live at next-ai.next-innovations.ltd", year: "—", role: "AI · Product",
    url: "https://next-ai.next-innovations.ltd",
    slug: "next-ai",
    detail: {
      client: "Next Innovations — own product",
      overview: "Public-facing AI assistant for Next Innovations customers. One conversational surface handles guided consultation, LINE AI setup, bookings, and quick actions, with a clear handoff to the human team when a conversation needs one.",
      highlights: [
        "Guided consultation flows that turn a visitor question into a concrete next step",
        "LINE AI setup and channel onboarding inside the conversation",
        "Bookings and quick actions handled directly by the assistant",
        "Human handoff path for conversations that need the team",
      ],
      stack: ["AI agents", "Web app", "LINE integration"],
    },
    mockups: {
      desktop: "/works/next-ai/next-ai-desktop.webp",
      tablet:  "/works/next-ai/next-ai-tablet.webp",
      mobile:  "/works/next-ai/next-ai-mobile.webp",
    },
    featured: true },
  /* Product & MVP Development */
  { ...PUBLISHED, cat: "mvp", name: "Tee-Up O'Clock App", sector: "Golf booking · MM/TH",
    blurb: "Mobile golf booking app for players in Myanmar and Thailand — course discovery, tee-time selection, reservations, and booking management shaped through Phase 1–2 product design.",
    metric: "Phase 1–2 · booking flows + design system", year: "—", role: "Product · Mobile", featured: true,
    slug: "tee-up-app",
    detail: {
      overview: "Player-facing golf booking experience designed for the Myanmar and Thailand markets. The product brings course discovery, tee-time availability, reservation details, and booking management into one mobile-first flow.",
      highlights: [
        "Course discovery and tee-time browsing for regional golfers",
        "Step-by-step reservation flow from date selection through booking confirmation",
        "Player booking history and reservation management",
        "Reusable components and responsive layouts defined across Phase 1–2",
      ],
      stack: ["Product design", "Mobile app", "Design system", "Booking workflows"],
      client: "Tee-Up O'Clock · Myanmar / Thailand",
    },
    mockups: {
      desktop: "/works/tee-up-app/tee-up-app-desktop.webp",
      tablet:  "/works/tee-up-app/tee-up-app-tablet.webp",
      mobile:  "/works/tee-up-app/tee-up-app-mobile.webp",
    } },
  { ...PUBLISHED, cat: "mvp", name: "Tee-Up O'Clock Admin", sector: "Golf operations · MM/TH",
    blurb: "Operations dashboard for the Tee-Up O'Clock booking platform — centralising reservations, course and tee-time inventory, customer records, and day-to-day booking oversight.",
    metric: "Reservations · inventory · operations", year: "—", role: "Product · Admin",
    slug: "tee-up-admin",
    detail: {
      overview: "Administrative companion to the Tee-Up O'Clock player app. The dashboard gives golf operators one place to monitor bookings, manage course and tee-time availability, review customer activity, and keep daily operations moving.",
      highlights: [
        "Booking overview for monitoring reservation status and daily activity",
        "Course, schedule, and tee-time inventory management",
        "Customer and reservation records available to operations teams",
        "Responsive dashboard layouts for desktop, tablet, and mobile workflows",
      ],
      stack: ["Product design", "Admin dashboard", "Design system", "Booking operations"],
      client: "Tee-Up O'Clock · Myanmar / Thailand",
    },
    mockups: {
      desktop: "/works/tee-up-admin/tee-up-admin-desktop.webp",
      tablet:  "/works/tee-up-admin/tee-up-admin-tablet.webp",
      mobile:  "/works/tee-up-admin/tee-up-admin-mobile.webp",
    } },

  /* Web Systems & Applications — descriptions sourced from the company
     knowledge base (Work Histories collection); mockups in /public/works. */
  { ...PUBLISHED, cat: "web", name: "KG Motors", sector: "Automotive · EV · Japan",
    blurb: "Corporate web platform for KG Motors, the Japanese EV startup behind mibot, a single-seat micro EV — corporate site, news, plus order/test-drive flows, user dashboard, and admin dashboards.",
    metric: "Live at kg-m.jp · user + admin dashboards", year: "—", role: "Web · System",
    slug: "kg-motors",
    detail: {
          "overview": "Corporate and product showcase site for KG Motors, the Hiroshima EV startup behind mibot — a single-seat micro EV built for an aging, depopulating Japan. The site carries the CEO's vision, the three pillars (fun, low cost, high energy efficiency), and routes visitors into mibot product pages, recruiting, and press resources.",
          "highlights": [
                "Vision and CEO messaging with cross-links to the mibot product site and note.com developer updates",
                "News, careers, press kit downloads, and governance pages",
                "Order and test-drive flows, user dashboard, and admin dashboards",
                "Clean, high-contrast Japanese industrial design direction"
          ],
          "stack": [
                "WordPress",
                "React (Studio.Design)",
                "Onamae hosting",
                "Figma"
          ],
          "client": "KG Motors Inc. · Japan"
    },
    url: "https://kg-m.jp/",
    mockups: {
      desktop: "/works/kg-motors/kg-motors-desktop.webp",
      tablet:  "/works/kg-motors/kg-motors-tablet.webp",
      mobile:  "/works/kg-motors/kg-motors-mobile.webp",
    },
    featured: true },
  { ...PUBLISHED, cat: "web", name: "Bluelife Insurance", sector: "Insurance aggregator · Myanmar",
    blurb: "Insurance comparison platform aggregating 10+ providers (AIA, Manulife, GGI Nippon Life…) — product catalogs, comparison engine, premium simulator, rankings, consultation leads via web, Viber and Messenger, and a Laravel partner/admin portal.",
    metric: "Live · 10+ partners · Laravel + React", year: "—", role: "Web · System",
    slug: "bluelife",
    detail: {
          "overview": "Insurance aggregator for the Myanmar market: one place to discover, compare, and simulate Life and General insurance from AIA, Manulife, GGI Nippon Life and 10+ partners, then convert research into a free consultation.",
          "highlights": [
                "Catalogs across Health, Education, Savings, Critical Illness, Motor, Fire, Personal Accident, Marine Cargo",
                "Comparison engine and premium simulator built as React islands inside Laravel Blade",
                "Rankings, reviews, partnered hospitals for trust",
                "Leads via web form, Viber, Messenger, phone; Laravel partner and admin portal",
                "Flexible product-attribute schema so each provider's spec fits without a rigid template"
          ],
          "stack": [
                "Laravel / PHP",
                "React",
                "MySQL / MariaDB",
                "AWS",
                "Viber + Messenger API",
                "Google Maps"
          ],
          "client": "Bluelife Insurance Agency · Myanmar"
    },
    mockups: {
      desktop: "/works/bluelife/bluelife-desktop.webp",
      tablet:  "/works/bluelife/bluelife-tablet.webp",
      mobile:  "/works/bluelife/bluelife-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Release Pet", sector: "Pet care · Tokyo",
    blurb: "Membership-based pet-sitting service site for Tokyo (Minato, Shibuya, Meguro) — course and subscription plans, 24/7 LINE/email inquiry flow, sitter matching and session reports, with WordPress for service pages and blog.",
    metric: "Live · LINE inquiry flow · subscriptions", year: "—", role: "Web · Service platform",
    slug: "release-pet",
    detail: {
          "overview": "Membership pet-sitting service for Tokyo's Minato, Shibuya and Meguro wards. Owners inquire over LINE or email, join the membership, get matched with a certified sitter, and receive a photo/video report after every session.",
          "highlights": [
                "Course plans: Standard 60 min, Walking 40 min, Overnight 12 h, plus monthly subscriptions",
                "24/7 LINE and email inquiry flow",
                "Sitter matching by location and care type; session reports to owners",
                "WordPress for service pages, guides, and blog; NDA-aware privacy handling for high-profile clients"
          ],
          "stack": [
                "React / Next.js",
                "Laravel",
                "WordPress",
                "Onamae hosting",
                "Figma"
          ],
          "client": "Release PET · Tokyo"
    },
    mockups: {
      desktop: "/works/release-pet/release-pet-desktop.webp",
      tablet:  "/works/release-pet/release-pet-tablet.webp",
      mobile:  "/works/release-pet/release-pet-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Release", sector: "Wellness · Japan",
    blurb: "Official website and coaching platform for Release LLC — Yayoi Enoki's \"beauty and health\" brand hub at yayoi-enoki.com, with the Yotsu-Doku Nuki method, bestselling book, online coaching gateways, and links to Release Pet.",
    metric: "Live at yayoi-enoki.com", year: "—", role: "Web · CMS",
    slug: "release",
    detail: {
          "overview": "Digital headquarters for Release LLC, founded and led by representative Yayoi Enoki under the philosophy \"Transforming the future through beauty and health.\" The site presents her profile, the bestselling \"Yotsu-Doku Nuki Diet\" book, online coaching and upcoming program services, and gateways to related wellness brands such as Release Pet.",
          "highlights": [
                "Brand manifesto home with book, news, and media highlights",
                "Service landing pages for online coaching and upcoming online programs",
                "Book pages that route readers into coaching application channels",
                "LINE, YouTube, Instagram, and Note integrations"
          ],
          "stack": [
                "WordPress (custom Release theme)",
                "PHP 8 / MySQL",
                "Onamae hosting",
                "Figma"
          ],
          "client": "Release LLC · Japan"
    },
    url: "https://yayoi-enoki.com/",
    mockups: {
      desktop: "/works/release/release-desktop.webp",
      tablet:  "/works/release/release-tablet.webp",
      mobile:  "/works/release/release-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Eat Retreat", sector: "Wellness coaching · Japan",
    blurb: "Landing page for EAT RETREAT — Yayoi Enoki's online coaching program built on her bestselling \"Yotsu-Doku Nuki Diet\" book, capturing signups through a LINE-first conversion funnel.",
    metric: "Live at eat-retreat.jp", year: "—", role: "Web · Landing",
    slug: "eat-retreat",
    detail: {
          "overview": "Landing page and program platform for EAT RETREAT, the online coaching service from Release LLC representative Yayoi Enoki — live at eat-retreat.jp. A companion to her bestselling \"Yotsu-Doku Nuki Diet\" book, the program coaches eliminating four dietary triggers — wheat, vegetable oil, dairy, and sweets — for sustainable health without extreme restriction.",
          "highlights": [
                "Book-to-coaching funnel: Amazon bestseller highlights into LINE registration",
                "Method explainer for the four Yotsu-Doku pillars, program preview, FAQ",
                "LINE Official Account as the primary lead-capture channel",
                "Mobile-first responsive layout tuned for waiting-list conversion"
          ],
          "stack": [
                "HTML/CSS/JS landing",
                "WordPress",
                "Vercel",
                "LINE Messaging API"
          ],
          "client": "Release LLC · Japan"
    },
    url: "https://eat-retreat.jp/",
    mockups: {
      desktop: "/works/eat-retreat/eat-retreat-desktop.webp",
      tablet:  "/works/eat-retreat/eat-retreat-tablet.webp",
      mobile:  "/works/eat-retreat/eat-retreat-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Phromon Japan", sector: "Wellness · Japan",
    blurb: "Official Japanese brand portal for PHROMON (本麗源) premium wellness products — Vue.js single-page site with product showcase, case studies, partner and member onboarding, and a gateway into the secure member portal.",
    metric: "Live at phromon.net · Vue.js SPA", year: "—", role: "Web · Frontend",
    slug: "phromon",
    detail: {
          "overview": "Official Japanese brand portal for PHROMON (本麗源) premium wellness and skincare products. A Vue.js single-page site covering brand philosophy, products, case studies, partner and member onboarding, and a gateway into the secure member portal at phromonjpn.com.",
          "highlights": [
                "Top, About, Product, Case, Partners, Join, and News sections",
                "Cross-domain hand-off to the authenticated member portal",
                "Swiper-driven product and case carousels",
                "Bespoke corporate showcase UI designed in Figma"
          ],
          "stack": [
                "Vue.js",
                "Element UI",
                "Webpack",
                "Swiper",
                "REST backend",
                "CDN hosting"
          ],
          "client": "PHROMON Japan · Tokyo"
    },
    url: "https://phromon.net/",
    mockups: {
      desktop: "/works/phromon/phromon-desktop.webp",
      tablet:  "/works/phromon/phromon-tablet.webp",
      mobile:  "/works/phromon/phromon-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "IIDA Electronics Myanmar", sector: "BPO · Manufacturing · MM/JP",
    blurb: "Bilingual EN/JP corporate site for Myanmar's largest Japanese-owned BPO and contract-manufacturing hub — facility and ISO 9001/27001 showcases, production achievements, and inquiry flows for Japanese enterprise clients.",
    metric: "Live at iida-imm.com · EN/JP", year: "—", role: "Web · CMS",
    slug: "iida-koe",
    detail: {
          "overview": "Bilingual EN/JP corporate site for IIDA Electronics Myanmar (IIDA KOE), the largest Japanese-owned BPO and contract-manufacturing hub in Myanmar, built to win enterprise inquiries from Japanese clients by putting certifications, capacity and infrastructure front and center.",
          "highlights": [
                "ISO 9001 / ISO 27001, 320+ staff, 24/7/365 operations, 450 KVA backup power showcased for offshore trust",
                "Production achievements and case studies: data processing, device manufacturing, CAD/BIM, VR",
                "EN/JP language toggle",
                "Inquiry and quotation forms, Google Maps for the Mingaladon Industrial Park HQ"
          ],
          "stack": [
                "WordPress",
                "Elementor",
                "PHP",
                "MySQL / MariaDB",
                "Google Analytics / GTM",
                "Google Maps"
          ],
          "client": "IIDA Electronics Myanmar Co., Ltd. · Myanmar / Japan"
    },
    url: "https://www.iida-imm.com/en/",
    mockups: {
      desktop: "/works/iida-koe/iida-koe-desktop.webp",
      tablet:  "/works/iida-koe/iida-koe-tablet.webp",
      mobile:  "/works/iida-koe/iida-koe-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "KINUJO", sector: "Beauty electronics · Japan",
    blurb: "Global brand and e-commerce portal for KINUJO's Haute-Tech Maison hair-care line — WordPress with WPML EN/JP, Alpine.js and Glide.js front end, and a full analytics, A/B-testing and ad-pixel stack.",
    metric: "Live at kinujo.jp · EN/JP · e-commerce", year: "—", role: "Web · CMS",
    slug: "kinujo",
    detail: {
          "overview": "Global brand and e-commerce portal for KINUJO, the Japanese Haute-Tech Maison hair-care brand — Silk Premium Dryer, Straight Iron, 2WAY and Curl Irons. EN/JP via WPML, with a full analytics, A/B testing and ad-pixel stack for global campaigns.",
          "highlights": [
                "WPML English/Japanese, Alpine.js + Glide.js front end",
                "Ptengine A/B testing, GA4, GTM, Matomo",
                "TikTok, Facebook, Google, Yahoo!, Twitter, Microsoft ad pixels",
                "reCAPTCHA, cookie compliance, A8.net affiliate, Contact Form 7"
          ],
          "stack": [
                "WordPress",
                "Alpine.js",
                "Glide.js",
                "PHP / MySQL",
                "Nginx",
                "Cloudflare CDN"
          ],
          "client": "KINUJO Inc. · Japan"
    },
    url: "https://kinujo.jp/en/",
    mockups: {
      desktop: "/works/kinujo/kinujo-desktop.webp",
      tablet:  "/works/kinujo/kinujo-tablet.webp",
      mobile:  "/works/kinujo/kinujo-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Triple A", sector: "Marketing agency · Tokyo",
    blurb: "Corporate site for AAA Co., Ltd., a Shibuya digital-marketing and CX consulting agency — services, partner branding (Google, Yahoo!, Meta), and inquiry, built on WordPress + Elementor.",
    metric: "Live at aaa-top.com", year: "2023", role: "Web · CMS",
    slug: "triple-a",
    detail: {
          "overview": "Corporate site for AAA Co., Ltd., a Shibuya digital-marketing, market-research and CX consulting agency founded in 2023. Presents services, Google / Yahoo! / Meta partner branding, and company profile, with an inquiry path for prospective clients.",
          "highlights": [
                "Services: digital advertising, internet research, CX consulting, ad design and production",
                "Partner logos and corporate profile with Google Maps for the Shibuya office",
                "All in One SEO configured for Japanese search"
          ],
          "stack": [
                "WordPress",
                "Elementor",
                "jQuery",
                "PHP",
                "AIOSEO",
                "Onamae hosting"
          ],
          "client": "AAA Co., Ltd. · Tokyo"
    },
    url: "https://aaa-top.com/",
    mockups: {
      desktop: "/works/triple-a/triple-a-desktop.webp",
      tablet:  "/works/triple-a/triple-a-tablet.webp",
      mobile:  "/works/triple-a/triple-a-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Focus Star", sector: "Trading & distribution · Myanmar",
    blurb: "Corporate website for Focus Star Group — JTI distribution partner, Wave Plus water, Optima Werkz automotive — company history, business scope, and contact, on WordPress behind Cloudflare.",
    metric: "Live at focusstar.com", year: "—", role: "Web · CMS",
    slug: "focus-star",
    detail: {
          "overview": "Corporate website for Focus Star Group, a Yangon trading and distribution company since 1974 — JTI tobacco manufacturing and distribution partner, Wave Plus purified water, and Optima Werkz automotive services.",
          "highlights": [
                "Company history timeline from Central Myanmar (1974) to Wave Plus (2018)",
                "Business scope pages per vertical",
                "Contact Form 7 inquiry, Yoast SEO",
                "Cloudflare CDN and edge security"
          ],
          "stack": [
                "WordPress",
                "Elementor",
                "PHP / MySQL",
                "Swiper + Isotope",
                "Cloudflare"
          ],
          "client": "Focus Star Co., Ltd. · Myanmar"
    },
    url: "https://www.focusstar.com/",
    mockups: {
      desktop: "/works/focus-star/focus-star-desktop.webp",
      tablet:  "/works/focus-star/focus-star-tablet.webp",
      mobile:  "/works/focus-star/focus-star-mobile.webp",
    } },
  { ...PUBLISHED, cat: "web", name: "Myanmar Social Finance (YIG)", sector: "Microfinance · Myanmar",
    blurb: "Microfinance site for Young Investment Group's Myanmar Social Finance arm — business, agriculture and group loans, loan application entry, mission and blog content, on the WordPress stack shared with yigmm.com.",
    metric: "Loan application flow · WordPress", year: "—", role: "Web · CMS",
    slug: "yig-msf",
    detail: {
          "overview": "Microfinance site for Young Investment Group's Myanmar Social Finance arm, on the WordPress stack shared with the yigmm.com conglomerate site. Presents business, agriculture and group loan products with a loan-application entry point, mission content, and blog.",
          "highlights": [
                "Loan product pages and application entry",
                "Mission, vision, and group heritage since 1998",
                "Google Tag Manager / Site Kit analytics, corporate inquiry forms"
          ],
          "stack": [
                "WordPress",
                "PHP",
                "jQuery",
                "Windows Server + IIS + Plesk",
                "Google Site Kit"
          ],
          "client": "Young Investment Group · Myanmar"
    },
    mockups: {
      desktop: "/works/yig-msf/yig-msf-desktop.webp",
      tablet:  "/works/yig-msf/yig-msf-tablet.webp",
      mobile:  "/works/yig-msf/yig-msf-mobile.webp",
    } },

  /* AI-Ready Websites & UI/UX */
  /* Our own corporate site — same deliberate own-product exception as
     Next AI above: it is the flagship demo of the AI-ready practice. */
  { ...PUBLISHED, cat: "uiux", name: "Next Innovations", sector: "Corporate · AI-ready",
    blurb: "Our own corporate site at next-innovations.ltd — AI-driven business and product development studio for Myanmar and Japanese teams, with an on-page Next AI assistant, structured content, and the design system it runs on.",
    metric: "Live at next-innovations.ltd", year: "—", role: "UI/UX · Web",
    url: "https://next-innovations.ltd",
    mockups: {
      desktop: "/works/next-innovations/next-innovations-desktop.webp",
      tablet:  "/works/next-innovations/next-innovations-tablet.webp",
      mobile:  "/works/next-innovations/next-innovations-mobile.webp",
    },
    featured: true },
  { ...PUBLISHED, cat: "web", name: "Clever", sector: "Business consulting · Japan",
    blurb: "Corporate site for CLEVERS Co., Ltd., a Tokyo business-support company — consulting, performance marketing, live-streaming and influencer promotion, and overseas expansion support, presented as four clear service lines on WordPress.",
    metric: "Live at clevers.top", year: "—", role: "Web · CMS",
    slug: "clever",
    detail: {
          "overview": "Corporate and service-showcase website for CLEVERS Co., Ltd. (Clevers株式会社), a Shibuya-based business-support company founded in 2016. The site positions the company as a strategic partner for launching ventures, improving marketing performance, live-streaming and influencer promotion, and expanding into China and Southeast Asia — its brand built around finding a “clever way” to solve business challenges.",
          "highlights": [
                "Four clearly separated service lines: consulting, marketing, live streaming, overseas expansion",
                "About Us with company history, leadership, and Cerulean Tower office access",
                "News timeline from establishment through live-streaming launch and site launch",
                "Responsive desktop and mobile layouts, corporate inquiry route",
          ],
          "stack": [
                "WordPress",
                "PHP",
                "Figma",
                "Onamae / AWS",
          ],
          "client": "CLEVERS Co., Ltd. · Japan",
    },
    url: "https://clevers.top/",
    mockups: {
      desktop: "/works/clever/clever-desktop.webp",
      tablet:  "/works/clever/clever-tablet.webp",
      mobile:  "/works/clever/clever-mobile.webp",
    } },
];

/* The works wall shows anything approved and clearable.
   The home preview shows the editorial subset of that. */
const isPublished = p => p.founderApproved === true && p.public === true;
const isOnHome = p => isPublished(p) && p.showOnHome === true;

const PUBLISHED_PROJECTS = PROJECTS.filter(isPublished);
const HOME_PROJECTS = PROJECTS.filter(isOnHome);

export { CATEGORIES, CAT, PROJECTS, PUBLISHED_PROJECTS, HOME_PROJECTS, isPublished, isOnHome };
